import { failNever } from '../utils/fail-never.js';
import { getContext, RequestContext } from '../runtime/request-context.js';
import { LogLevel } from './log-level.js';

export interface Logger {
  info(
    message: string,
    ...values: Record<
      string,
      string | number | boolean | Date | null | undefined
    >[]
  ): void;
  debug(
    message: string,
    ...values: Record<
      string,
      string | number | boolean | Date | null | undefined
    >[]
  ): void;
  error(
    message: string,
    ...values: Record<
      string,
      string | number | boolean | Date | null | undefined
    >[]
  ): void;
  warn(
    message: string,
    ...values: Record<
      string,
      string | number | boolean | Date | null | undefined
    >[]
  ): void;
  log(
    level: LogLevel,
    message: string,
    ...values: Record<string, string | number>[]
  ): void;
  raw(message: string): void;
}

export interface LogEntry {
  severity: string;
  message: string;
  time: string;
  logger: string;
  requestId?: string;
  traceId?: string;
  spanId?: string;
  data?: unknown;
  [key: string]: unknown;
}

export type LogEnricher = (
  entry: LogEntry,
  ctx: RequestContext | undefined,
) => void;

export type LogFormat = 'pretty' | 'json';

let allowedLevels = ['INFO', 'WARN', 'ERROR', 'DEBUG'];
let lastLogTime = new Date().getTime();
let logFormat: LogFormat = 'pretty';
const enrichers: LogEnricher[] = [];

export function setLogFormat(format: LogFormat): void {
  logFormat = format;
}

export function registerLogEnricher(enricher: LogEnricher): void {
  enrichers.push(enricher);
}

export function clearLogEnrichers(): void {
  enrichers.length = 0;
}

function severityFor(level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR'): string {
  if (level === 'WARN') {
    return 'WARNING';
  }
  return level;
}

export function setLogLevel(level: LogLevel | 'NONE') {
  if (level === 'DEBUG') {
    allowedLevels = ['INFO', 'WARN', 'ERROR', 'DEBUG'];
  } else if (level === 'INFO') {
    allowedLevels = ['INFO', 'WARN', 'ERROR'];
  } else if (level === 'WARN') {
    allowedLevels = ['WARN', 'ERROR'];
  } else if (level === 'ERROR') {
    allowedLevels = ['ERROR'];
  } else if (level === 'NONE') {
    allowedLevels = [];
  } else {
    failNever(level, 'unknown log level');
  }
}

export function createLogger(name: string): Logger {
  function log(
    level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR',
    message: string,
    ...values: any[]
  ): void {
    if (!message) {
      return;
    }

    if (allowedLevels.indexOf(level) === -1) {
      return;
    }

    const now = new Date();
    const ctx = getContext();

    if (logFormat === 'json') {
      const data =
        values.length === 0
          ? undefined
          : values.length === 1
            ? values[0]
            : Object.assign({}, ...values);

      const entry: LogEntry = {
        severity: severityFor(level),
        message,
        time: now.toISOString(),
        logger: name,
      };

      if (ctx?.requestId) {
        entry.requestId = ctx.requestId;
      }
      if (ctx?.traceId) {
        entry.traceId = ctx.traceId;
      }
      if (ctx?.spanId) {
        entry.spanId = ctx.spanId;
      }
      if (data) {
        entry.data = data;
      }

      for (const enricher of enrichers) {
        enricher(entry, ctx);
      }

      const line = JSON.stringify(entry);
      if (level === 'ERROR') {
        console.error(line);
      } else if (level === 'WARN') {
        console.warn(line);
      } else if (level === 'DEBUG') {
        console.debug(line);
      } else {
        console.info(line);
      }

      lastLogTime = now.getTime();
      return;
    }

    const reqTag = ctx?.requestId
      ? ` [req=${ctx.requestId.substring(0, 8)}]`
      : '';

    const prefix =
      `[${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}] ${level.padEnd(5, ' ')} : ` +
      ` ${(now.getTime() - lastLogTime).toString().padStart(4, ' ')}ms - ` +
      name.substring(0, 20).padEnd(20, ' ') +
      reqTag +
      ' - ';

    let i = 0;
    for (const messagePart of `${message}`.split('\n')) {
      const value = prefix + messagePart;
      const args = i === 0 ? values : [];
      if (level === 'DEBUG') {
        console.debug(value, ...args);
      } else if (level === 'ERROR') {
        console.error(value, ...args);
      } else if (level === 'WARN') {
        console.warn(value, ...args);
      } else if (level === 'INFO') {
        console.info(value, ...args);
      } else {
        failNever(level, 'unknown log level');
      }
      i++;
    }

    lastLogTime = now.getTime();
  }

  return {
    log,
    raw(message: string) {
      console.log(message);
    },
    info(message: string, ...values) {
      log('INFO', message, ...values);
    },
    debug(message: string, ...values) {
      log('DEBUG', message, ...values);
    },
    error(message: string, ...values) {
      log('ERROR', message, ...values);
    },
    warn(message: string, ...values) {
      log('WARN', message, ...values);
    },
  };
}
